export interface BoxData {
  id: number;
  name: string;
  description: string;
  sortOrder: number;
  purchaseCost: number;
  shippingCost: number;
  storageCost: number;
  count: number;
  imageUrl: string;
  dimensions: Dimensions;
  returnFee: boolean;
  chargeShipping: boolean;
}

export interface Dimensions {
  depth: number;
  height: number;
  width: number;
  weight: number;
}

export interface Discount {
  deletedAt: number;
  createdAt: number;
  updatedAt: number;
  discountCode: string;
  discountPercentage: number;
  expires: number;
  recurring: boolean;
  upFront: boolean;
  id: number;
}

export interface DiscountAndCount extends Discount {
  count: number;
}

export interface MakeOrderResponse {
  order: Order;
  paymentIntentSecret?: string;
  setupIntentSecret?: string;
  jwtSecret: string;
  paymentStatus: PaymentStatus;
}

export enum PaymentStatus {
  succeeded = 'succeeded',
  requiresAction = 'requires_action',
  requiresPaymentMethod = 'requires_payment_method',
  trialing = 'trialing',
  noneNeeded = 'none_needed',
  duplicatePrevented = 'duplicate_prevented',
}

export interface MakePaymentRequest {
  orderId: number;
  paymentMethodId: string;
}

export interface UpdatePaymentRequest {
  paymentMethodId: string;
}

export interface MakePaymentResponse {
  session: string;
}

export interface BoxCount {
  [key: number]: number;
}

export interface BookingParams {
  route: string;
}

export interface MakeOrderRequest {
  city?: string;
  boxes: BoxCount;
  collectionAddress: string;
  deliveryAddress?: string;
  collectionDate: number;
  deliveryDate?: number;
  roomPack: boolean;
  storageRequired: boolean;
  discountCode?: string;
  note: string;
  paymentMethodId: string;
  collectionCountry?: String;
  deliveryCountry?: String;
  collectionPostcode?: String;
  deliveryPostcode?: String;
}

export interface MakeSuitcaseOrderRequest {
  city?: string;
  collectionAddress: string;
  deliveryAddress?: string;
  collectionDate: number;
  deliveryDate?: number;
  suitcases: Dimensions[];
  discountCode?: string;
  note: string;
  paymentMethodId: string;
  country?: String;
  collectionCountry?: String;
  deliveryCountry?: String;
  collectionPostcode?: String;
  deliveryPostcode?: String;
}

export interface Order extends MakeOrderRequest {
  id: number;
  createdAt: number;
  updatedAt: number;
  deletedAt?: number;
  payments: PaymentResponse[];
  statuses: StatusResponse[];
  customer: UserResponse;
  orderBoxes: OrderBoxesResponse[];
  initialCost: number;
  monthlyCost: number;
  customerId: number;
  orderComplete: boolean;
  roomPackIncluded?: boolean;
  suitcaseOrder: boolean;
  notes: Note[];
  collectionCountry?: String;
  deliveryCountry?: String;
  collectionPostcode?: String;
  deliveryPostcode?: String;
  
  // Removal order fields (optional for regular orders)
  orderType?: 'removal';
  tierId?: string;
  tierName?: string;
  tierPrice?: number;
  propertyFloor?: string;
  propertyHasLift?: boolean | null;
  propertyHasFurniture?: boolean;
  propertyHasParking?: boolean;
  collectionCity?: string;
  deliveryCity?: string;
  moveDate?: number;
  packingMaterialsId?: string | null;
  packingMaterialsPrice?: number | null;
  packingMaterialsDetails?: string | null;
  storageId?: string | null;
  storageMonthlyPrice?: number | null;
  storageSize?: string | null;
  baseCost?: number;
  extrasCost?: number;
  totalInitialCost?: number;
  monthlyStorageCost?: number;
  discountPercentage?: number | null;
  discountApplied?: number;
  status?: string;
  stripePurchaseId?: string | null;
  stripeSubscriptionId?: string | null;
}

export interface Note {
  id: number;
  orderId: number;
  text: string;
  updatedAt: number;
  createdAt: number;
}

export interface OrderResponse {
  order: Order;
}

export interface OrderListResponse {
  orders: Order[];
}

export interface PreviousOrderListResponse {
  previousOrders: Order[];
  nextPayments?: { [orderId: number]: NextPaymentResponse };
}

export interface RemovalOrdersResponse {
  statusCode: number;
  orders: Order[];
  nextPayments?: { [orderId: number]: NextPaymentResponse };
}

export interface RemovalQuoteRequest {
  id: number;
  customerId?: number;
  tierType: string;
  tierName: string;
  customerEmail: string;
  customerPhone: string;
  message: string;
  status: string;
  quotedPrice?: number | null;
  quotedAt?: number | null;
  quotedByUserId?: number | null;
  createdAt: number;
  updatedAt: number;
  customer?: Customer;
  itemType?: 'quote'; // Helper to identify in merged lists
}

export interface RemovalQuoteRequestsResponse {
  statusCode: number;
  quotes: RemovalQuoteRequest[];
}

export interface StatusResponse {
  id: number;
  orderId: number;
  customerId: number;
  status: { id: number; type: string }; //Do this rather than make a new model
  statusId: number;
  updatedAt: number;
}

export interface OrderBoxesResponse {
  id: number;
  barcode: string;
  boxId: number;
  orderId: number;
  box: BoxData;
  dimensions: Dimensions;
  shippingCost: number;
}

export interface PaymentResponse {
  id: number;
  orderId: number;
  customerId: number;
  amount: number;
  succeeded: boolean;
  createdAt: number;
  stripeId?: string;
  status: string;
}

export interface Customer {
  orders: Order[];
  payments?: PaymentResponse[];
  id: number;
  createdAt: number;
  updatedAt: number;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  stripeId: string;
  phoneNumber?: string;
}

export interface UserResponse {
  id: number;
  createdAt: number;
  updatedAt: number;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  admin?: boolean;
  role?: string;
}

export interface MyAccountResponse {
  totalBoxDays: number;
  membershipTier?: string;
  jwt: string;
  customer: {
    id: number;
    createdAt: number;
    updatedAt: number;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber?: string;
    stripeId?: string;
    hubspotId?: number;
    orders?: Order[];
    payments?: PaymentResponse[];
  };
}

export interface CustomerListResponse {
  customers: Customer[];
}

export interface CustomerResponse {
  customer: Customer;
}

export interface UserListResponse {
  users: UserResponse[];
}

export interface DiscountResponse {
  discount: Discount;
}

export interface ProductResponse {
  product: Product;
}

export interface DiscountListResponse {
  discounts: DiscountAndCount[];
}

export interface ProductAndCount extends Product {
  count: number;
}

export interface ProductListResponse {
  products: ProductAndCount[];
}

export interface Product extends BoxData {
  createdAt: number;
  emailField: string;
  updatedAt: number;
}

export interface CheckoutProduct {
  name?: string;
  id?: number;
  price: string;
  brand: string;
  category: string;
  quantity: number;
  affiliation: string;
  coupon?: string;
  discount?: string;
  currency?: string;
}

export interface CheckoutItem {
  item_id?: string;
  item_name: string;
  affiliation: string;
  coupon?: string;
  currency: string;
  discount?: string;
  index: number;
  item_brand: string;
  item_category: string;
  price: string;
  quantity: string;
}

export interface BankHolidays {
  'england-and-wales': BankHolidayDivision;
  'northern-ireland': BankHolidayDivision;
  scotland: BankHolidayDivision;
}

interface BankHolidayDivision {
  division: string;
  events: BankHoliday[];
}

export interface BankHoliday {
  bunting: boolean;
  date: string;
  notes: string;
  title: string;
}

export enum BoxChangeType {
  ADDED = 'ADDED',
  REMOVED = 'REMOVED',
}

export enum BoxChangeSource {
  HUBSPOT = 'HUBSPOT',
  SYSTEM = 'SYSTEM',
  MANUAL = 'MANUAL',
}

export interface BoxChange {
  id: number;
  customerId: number;
  orderId: number;
  orderBoxId?: number;
  boxId: number;
  changeType: BoxChangeType;
  boxName: string;
  barcode?: string;
  source: BoxChangeSource;
  reason?: string;
  changedBy?: string;
  createdAt: number;
  updatedAt: number;
}

export interface BoxChangesResponse {
  statusCode: number;
  boxChanges: BoxChange[];
}

export interface NextPaymentResponse {
  statusCode: number;
  nextPaymentDate: number;
  amount: number;
  currency: string;
  status: string;
}
